import {
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  CardComponent,
  DocumentUploadComponent,
  type DocumentAttachment,
} from '@sportello-balneari/shared-components';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SelectModule } from 'primeng/select';
import { of } from 'rxjs';
import {
  ApplicantPersistency,
  ApplicantAttachment,
} from '../../../api/applicantPersistency';
import { CompilingApiService } from '../../../api/compiling-api.service';
import { nationalityOptions } from '../../../common/nationalityOptions';
import { TenderContextService } from '../../../services/compiling/state/tender-context.service';
import { AnswerAttachmentsService } from '../../../services/compiling/state/answer-attachments.service';
import { AnswerPermissionsService } from '../../../services/compiling/state/answer-permissions.service';
import { EnrichedSlotMetaComponent } from '../enriched-slot-meta.component';
import {
  requirementsToSlots,
  type EnrichedSlot,
} from '../requirements-to-slots';
import {
  deleteAttachmentFn,
  migrateUnknownAttachments,
  reindexAttachmentFn,
  uploadAndIndexFn,
} from '../utils';

@Component({
  selector: 'lib-applicant-form',
  standalone: true,
  imports: [
    SelectModule,
    InputTextModule,
    RadioButtonModule,
    ReactiveFormsModule,
    ButtonModule,
    DatePickerModule,
    CardComponent,
    FormsModule,
    DocumentUploadComponent,
    EnrichedSlotMetaComponent,
  ],
  template: `
    <div class="mx-auto flex w-full max-w-screen-lg flex-col gap-6">
      <lib-card>
        <ng-container title>Candidato</ng-container>
        <ng-container content>
          @let selectedApplicant = this.selectedApplicant();
          @if (selectedApplicant) {
            @if (
              selectedApplicant.type === 'Private' && selectedApplicant.private;
              as p
            ) {
              <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div class="flex flex-col gap-2">
                  <label class="font-semibold text-gray-700" for="fullName"
                    >Nome</label
                  >
                  <input
                    pInputText
                    id="fullName"
                    type="text"
                    [ngModel]="p.firstName + ' ' + p.lastName"
                    [disabled]="true"
                    class="w-full bg-gray-50 text-gray-900 opacity-100"
                  />
                </div>
                <div class="flex flex-col gap-2">
                  <label class="font-semibold text-gray-700" for="fiscalCode"
                    >Codice fiscale</label
                  >
                  <input
                    id="fiscalCode"
                    pInputText
                    type="text"
                    [ngModel]="p.fiscalCode"
                    [disabled]="true"
                    class="w-full bg-gray-50 text-gray-900 opacity-100"
                  />
                </div>
                <div class="flex flex-col gap-2">
                  <label class="font-semibold text-gray-700" for="dateOfBirth"
                    >Data di nascita</label
                  >
                  <input
                    id="dateOfBirth"
                    pInputText
                    type="text"
                    [ngModel]="p.dateOfBirth"
                    [disabled]="true"
                    class="w-full bg-gray-50"
                  />
                </div>
                <div class="flex flex-col gap-2">
                  <label class="font-semibold text-gray-700" for="placeOfBirth"
                    >Luogo di nascita</label
                  >
                  <input
                    id="placeOfBirth"
                    pInputText
                    type="text"
                    [ngModel]="p.placeOfBirth"
                    [disabled]="true"
                    class="w-full bg-gray-50"
                  />
                </div>
                <div class="flex flex-col gap-2">
                  <label class="font-semibold text-gray-700" for="citizenship"
                    >Cittadinanza</label
                  >
                  <input
                    id="citizenship"
                    pInputText
                    type="text"
                    [ngModel]="p.citizenship"
                    [disabled]="true"
                    class="w-full bg-gray-50"
                  />
                </div>
                <div class="flex flex-col gap-2">
                  <label class="font-semibold text-gray-700" for="address"
                    >Indirizzo</label
                  >
                  <input
                    id="address"
                    pInputText
                    type="text"
                    [ngModel]="p.address"
                    [disabled]="true"
                    class="w-full bg-gray-50"
                  />
                </div>
              </div>
            }

            @if (
              selectedApplicant.type === 'Company' && selectedApplicant.company;
              as c
            ) {
              <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div class="flex flex-col gap-2">
                  <label class="font-semibold text-gray-700" for="companyName"
                    >Ragione sociale</label
                  >
                  <input
                    pInputText
                    id="companyName"
                    type="text"
                    [ngModel]="c.companyName"
                    [disabled]="true"
                    class="w-full bg-gray-50 text-gray-900 opacity-100"
                  />
                </div>
                <div class="flex flex-col gap-2">
                  <label class="font-semibold text-gray-700" for="vatNumber"
                    >Partita IVA</label
                  >
                  <input
                    pInputText
                    id="vatNumber"
                    type="text"
                    [ngModel]="c.vatNumber"
                    [disabled]="true"
                    class="w-full bg-gray-50 text-gray-900 opacity-100"
                  />
                </div>
                <div class="flex flex-col gap-2">
                  <label class="font-semibold text-gray-700" for="taxId"
                    >Codice fiscale</label
                  >

                  <input
                    pInputText
                    id="taxId"
                    type="text"
                    [ngModel]="c.taxId"
                    [disabled]="true"
                    class="w-full bg-gray-50"
                  />
                </div>
                <div class="flex flex-col gap-2">
                  <label
                    class="font-semibold text-gray-700"
                    for="registeredOffice"
                    >Sede legale</label
                  >
                  <input
                    pInputText
                    id="registeredOffice"
                    type="text"
                    [ngModel]="c.registeredOffice"
                    [disabled]="true"
                    class="w-full bg-gray-50"
                  />
                </div>
              </div>
            }
          }
        </ng-container>
      </lib-card>

      @if (mode() === 'full') {
        @for (item of enrichedDocumentSlots(); track item.slot.id) {
          <lib-document-upload
            [slot]="item.slot"
            [uploadFn]="uploadFn(item.slot.id)"
            [deleteFn]="deleteFn"
            [reindexFn]="reindexFn"
            [readonly]="!permissions.canEditAdministrativeOffer()"
            [value]="getSlot(item.slot.id)"
            (valueChange)="setSlot(item.slot.id, $event)"
          >
            <lib-enriched-slot-meta [item]="item" />
          </lib-document-upload>
        }
      }
    </div>
  `,
})
export class ApplicantFormComponent {
  /** In detached mode only the candidate info is shown — administrative
   *  document upload slots are hidden (there is no tender to require them). */
  readonly mode = input<'full' | 'detached'>('full');

  private readonly fb = inject(FormBuilder);
  private readonly applicantPersistency = inject(ApplicantPersistency);
  protected readonly tenderContext = inject(TenderContextService);
  protected readonly attachments = inject(AnswerAttachmentsService);
  protected readonly compilingApiService = inject(CompilingApiService);
  protected readonly permissions = inject(AnswerPermissionsService);

  private static readonly DEFAULT_ENRICHED_SLOTS: EnrichedSlot[] = [
    {
      slot: {
        id: 'domanda-partecipazione',
        label: 'Domanda di partecipazione',
        multiple: true,
      },
      members: [],
    },
    {
      slot: {
        id: 'dichiarazione-sopralluogo',
        label: 'Dichiarazione di avvenuto sopralluogo',
        multiple: true,
      },
      members: [],
    },
    {
      slot: {
        id: 'altri-documenti',
        label: 'Altri documenti (p. es. Visura camerale)',
        multiple: true,
      },
      members: [],
    },
  ];

  protected readonly enrichedDocumentSlots = computed<EnrichedSlot[]>(() => {
    const tenderInfo = this.tenderContext.tenderInfo();
    const envelopeA = tenderInfo?.extractedRequirements?.envelopeA;
    if (!envelopeA?.length)
      return ApplicantFormComponent.DEFAULT_ENRICHED_SLOTS;

    return [
      ...requirementsToSlots(envelopeA, tenderInfo?.attachments ?? []),
      {
        slot: {
          id: 'altri-documenti',
          label: 'Altri documenti',
          multiple: true,
        },
        members: [],
      },
    ];
  });

  protected readonly uploadFn = uploadAndIndexFn(
    this.compilingApiService,
    'ENVELOPE_A',
    () => this.tenderContext.tenderFormAnswer()?.id ?? '',
  );

  protected readonly deleteFn = deleteAttachmentFn(this.compilingApiService);
  protected readonly reindexFn = reindexAttachmentFn(this.compilingApiService);

  private readonly knownSlotIds = computed(
    () => new Set(this.enrichedDocumentSlots().map((item) => item.slot.id)),
  );

  protected getSlot(id: string): DocumentAttachment[] {
    return this.attachments.envelopeAAttachments()[id] ?? [];
  }

  protected setSlot(id: string, files: DocumentAttachment[]): void {
    this.attachments.envelopeAAttachments.update((v) => ({
      ...v,
      [id]: files,
    }));
  }

  applicants = rxResource({
    defaultValue: [] as any[],
    stream: () => this.applicantPersistency.getApplicants(),
  });

  private externalId = signal<string | null>(null);
  applicantResource = rxResource({
    stream: () => {
      const id = this.externalId();
      if (!id) return of(undefined);
      return this.applicantPersistency.getApplicantById(id);
    },
  });

  protected selectedApplicant = signal<any | undefined>(undefined);
  showApplicantForm = signal(false);
  readonly nationalityOptions = nationalityOptions;
  maxDate: Date = new Date();
  private newApplicantId = signal<string | undefined>(undefined);

  readonly typeOptions = [
    { label: 'Privato', value: 'PRIVATO' },
    { label: 'Azienda', value: 'AZIENDA' },
  ];

  createApplicantForm = this.fb.group({
    tipo: ['AZIENDA', [Validators.required]],
    nome: ['', [Validators.required]],
    cognome: ['', [Validators.required]],
    dateOfBirth: [null as Date | null, [Validators.required]],
    comuneNascita: ['', [Validators.required]],
    cittadinanza: ['', [Validators.required]],
    indirizzoResidenza: ['', [Validators.required]],
    taxId: ['', [Validators.required]],
    ragioneSociale: [''],
    piva: [''],
    indirizzoSede: [''],
    registrationCertificate: [null as ApplicantAttachment | null],
  });

  constructor() {
    migrateUnknownAttachments(
      this.attachments.envelopeAAttachments,
      this.knownSlotIds,
    );

    const currentId =
      this.tenderContext.applicantForm.controls.applicantId.value;
    if (currentId) this.externalId.set(currentId);

    this.createApplicantForm.get('tipo')?.valueChanges.subscribe((v) => {
      this.updateValidators(v || 'PRIVATO');
    });

    effect(() => {
      const applicant = this.applicantResource.value() as any;
      if (applicant) this.updateSelection(applicant);
    });

    effect(() => {
      const newId = this.newApplicantId();
      if (!this.applicants.isLoading() && newId) {
        this.tenderContext.applicantForm.patchValue({ applicantId: newId });
        this.newApplicantId.set(undefined);
      }
    });

    this.tenderContext.applicantForm.controls.applicantId.valueChanges.subscribe(
      (id) => {
        if (!id) {
          this.selectedApplicant.set(undefined);
          return;
        }
        const found = this.applicants.value()?.find((a: any) => a.id === id);
        if (found) this.updateSelection(found);
        else this.externalId.set(id);
      },
    );
  }

  openApplicantForm() {
    this.createApplicantForm.reset({ tipo: 'AZIENDA' });
    this.updateValidators('AZIENDA');
    this.showApplicantForm.set(true);
  }

  closeApplicantForm() {
    this.showApplicantForm.set(false);
  }

  private updateValidators(type: string): void {
    const isPrivato = type === 'PRIVATO';
    const form = this.createApplicantForm;
    const privatoFields = [
      'nome',
      'cognome',
      'dateOfBirth',
      'comuneNascita',
      'cittadinanza',
      'indirizzoResidenza',
    ];
    const aziendaFields = [
      'ragioneSociale',
      'piva',
      'indirizzoSede',
      'registrationCertificate',
    ];

    if (isPrivato) {
      aziendaFields.forEach((f) => {
        form.get(f)?.clearValidators();
        form.get(f)?.setValue('', { emitEvent: false });
      });
      privatoFields.forEach((f) =>
        form.get(f)?.setValidators([Validators.required]),
      );
    } else {
      privatoFields.forEach((f) => {
        form.get(f)?.clearValidators();
        form.get(f)?.setValue('', { emitEvent: false });
      });
      aziendaFields.forEach((f) =>
        form.get(f)?.setValidators([Validators.required]),
      );
    }
    form.get('taxId')?.setValidators([Validators.required]);
    [...privatoFields, ...aziendaFields, 'taxId'].forEach((f) =>
      form.get(f)?.updateValueAndValidity({ emitEvent: false }),
    );
  }

  private updateSelection(applicant: any) {
    this.selectedApplicant.set(applicant);
    const isPrivate = applicant.type === 'Private';
    this.tenderContext.applicantForm.patchValue(
      {
        applicantTaxId: isPrivate
          ? applicant.private.fiscalCode
          : applicant.company.taxId || applicant.company.vatNumber,
        applicantCompanyName: isPrivate
          ? `${applicant.private.firstName} ${applicant.private.lastName}`
          : applicant.company.companyName,
        applicantType: applicant.type,
      },
      { emitEvent: false },
    );
  }

  createApplicant(): void {
    if (this.createApplicantForm.invalid) {
      this.createApplicantForm.markAllAsTouched();
      return;
    }

    const val = this.createApplicantForm.getRawValue();
    const isPrivato = val.tipo === 'PRIVATO';

    const payload = {
      type: isPrivato ? 'Private' : 'Company',
      private: isPrivato
        ? {
            firstName: val.nome,
            lastName: val.cognome,
            dateOfBirth: val.dateOfBirth
              ? new Date(val.dateOfBirth).toISOString().split('T')[0]
              : null,
            placeOfBirth: val.comuneNascita,
            citizenship: val.cittadinanza,
            fiscalCode: (val.taxId ?? '').toUpperCase().trim(),
            address: val.indirizzoResidenza,
          }
        : null,
      company: !isPrivato
        ? {
            companyName: (val.ragioneSociale ?? '').trim(),
            vatNumber: (val.piva ?? '').toUpperCase().trim(),
            taxId: (val.taxId ?? '').toUpperCase().trim(),
            registeredOffice: val.indirizzoSede,
          }
        : null,
    };

    this.applicantPersistency.createApplicant(payload as any).subscribe({
      next: (response: any) => {
        this.applicants.reload();
        this.newApplicantId.set(response.id);
        this.showApplicantForm.set(false);
        this.createApplicantForm.reset({ tipo: 'AZIENDA' });
      },
      error: (err: any) => {
        console.error('Errore dettagliato:', err.error);
      },
    });
  }
  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.handleFile(input.files[0]);
    }
  }

  onFileDrop(event: DragEvent): void {
    event.preventDefault();
    if (event.dataTransfer?.files?.length) {
      this.handleFile(event.dataTransfer.files[0]);
    }
  }

  private handleFile(file: File): void {
    // if (file.type === 'application/pdf') {
    //   const attachment: ApplicantAttachment = {
    //     id: '',
    //     fileName: file.name,
    //     url: '',
    //     uploadedAt: new Date(),
    //   };
    //   this.createApplicantForm.patchValue({
    //     registrationCertificate: attachment,
    //   });
    // }
    this.applicantPersistency
      .uploadApplicantAttachment(this.selectedApplicant()?.id, file)
      .subscribe({
        next: (attachment) => {
          this.createApplicantForm.patchValue({
            registrationCertificate: attachment,
          });
        },
        error: (err) => {
          console.error("Errore durante l'upload del file:", err);
        },
      });
  }

  removeFile(event: Event): void {
    event.stopPropagation();
    this.createApplicantForm.patchValue({
      registrationCertificate: null,
    });
  }
}
